import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  PUBLIC_GENERATIONS_PER_PRESET_PER_DAY,
  PUBLIC_GENERATIONS_TOTAL_PER_DAY,
} from '@/constants/common';
import type { Preset } from '@/constants/presets';
import { useAnalytics } from '@/contexts/Analytics';
import { errorMessage } from '@/utils/errorMessage';
import axiosInstance from '@/utils/httpClient';
import { getLanguageName } from '@/utils/language';
import { createClient } from '@/utils/supabase/component';
import { wasErrorToastShown } from '@/utils/toastError';

import { useTemplateTranslation } from './kit';
import {
  readStored,
  useTemplateFields,
  writeStored,
} from './useTemplateFields';

import { CaptionItem } from '@/types/Caption';

export type UploadedFile = { name: string; path: string; preview?: string };

const today = () => new Date().toISOString().split('T')[0];

/**
 * Everything the free template workspace does: field values, the daily free
 * limits, uploads, and the async generation round trip (/api/public-generate
 * returns placeholder ids that are polled until done).
 *
 * Stored values (language, today's usage) load after mount, so the first
 * client render matches the server render.
 */
export function usePublicGeneration(preset: Preset) {
  const { t, lang } = useTemplateTranslation();
  const analytics = useAnalytics();
  const slug = preset.slug;
  const { fields, setField, clearFields } = useTemplateFields(preset);

  // Mirrors the server caps in /api/public-generate (the real gate); a
  // preset's own `maxGenerations` can only lower the per-template number.
  const maxGenerations = Math.min(
    preset.maxGenerations || PUBLIC_GENERATIONS_PER_PRESET_PER_DAY,
    PUBLIC_GENERATIONS_PER_PRESET_PER_DAY,
  );
  const maxTotalGenerations = PUBLIC_GENERATIONS_TOTAL_PER_DAY;

  // Runs per template per day and across templates per day. Both reset
  // daily, like the server's per-IP caps they mirror.
  const templateUsageKey = `generatedCaptions-${slug}-${today()}`;
  const totalUsageKey = `totalGeneratedCaptions-${today()}`;
  const [usedForTemplate, setUsedForTemplate] = useState(0);
  const [usedInTotal, setUsedInTotal] = useState(0);

  const [language, setLanguageState] = useState(
    getLanguageName(lang) || 'English',
  );
  const [numberOfOutputs, setNumberOfOutputs] = useState(1);
  const [maxOutputs, setMaxOutputs] = useState(preset.outputs || 5);
  const [captions, setCaptions] = useState<CaptionItem[]>([]);
  const [shouldUpgrade, setShouldUpgrade] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    setUsedForTemplate(Number(readStored(templateUsageKey)) || 0);
    setUsedInTotal(Number(readStored(totalUsageKey)) || 0);
    const storedLanguage = readStored('language');
    if (typeof storedLanguage === 'string' && storedLanguage) {
      setLanguageState(storedLanguage);
      if (storedLanguage !== 'English') setMaxOutputs(1);
    }
  }, [templateUsageKey, totalUsageKey]);

  const reachedTotalLimit = usedInTotal >= maxTotalGenerations;
  const reachedTemplateLimit = usedForTemplate >= maxGenerations;
  const runsLeft = Math.max(
    0,
    Math.min(
      maxGenerations - usedForTemplate,
      maxTotalGenerations - usedInTotal,
    ),
  );

  const setLanguage = useCallback(
    (value: string) => {
      setLanguageState(value);
      writeStored('language', value);
      // Several outputs are an English-only option.
      if (value !== 'English') {
        setNumberOfOutputs(1);
        setMaxOutputs(1);
      } else {
        setMaxOutputs(preset.outputs || 5);
      }
    },
    [preset.outputs],
  );

  // Uploads go straight to the public `uploads` bucket with the anon key;
  // the generation request only carries the storage path.
  const supabase = useMemo(() => createClient(), []);
  const [uploadedFiles, setUploadedFiles] = useState<
    Record<string, UploadedFile>
  >({});
  const [uploadedMultiFiles, setUploadedMultiFiles] = useState<
    Record<string, any>
  >({});
  const [isFileLoading, setIsFileLoading] = useState(false);

  const uploadFile = useCallback(
    async (fieldName: string, file: File) => {
      setIsFileLoading(true);
      try {
        const extension = file.name.split('.').pop();
        const path = `${slug}/${Date.now()}.${extension}`;
        const { error } = await supabase.storage
          .from('uploads')
          .upload(path, file);
        if (error) {
          toast.error(t('File upload failed'));
          return;
        }
        setUploadedFiles((current) => ({
          ...current,
          [fieldName]: {
            name: file.name,
            path,
            preview: file.type.startsWith('image/')
              ? URL.createObjectURL(file)
              : undefined,
          },
        }));
        setField(fieldName, path);
      } finally {
        setIsFileLoading(false);
      }
    },
    [setField, slug, supabase, t],
  );

  const removeFile = useCallback(
    (fieldName: string) => {
      setUploadedFiles((current) => {
        const next = { ...current };
        const preview = next[fieldName]?.preview;
        if (preview) URL.revokeObjectURL(preview);
        delete next[fieldName];
        return next;
      });
      setField(fieldName, '');
    },
    [setField],
  );

  const setMultiFiles = useCallback(
    (fieldName: string, files: any) => {
      setUploadedMultiFiles((current) => ({ ...current, [fieldName]: files }));
      setField(fieldName, files);
    },
    [setField],
  );

  const mutation = useMutation(
    (body: Record<string, unknown>) =>
      axiosInstance.post('/api/public-generate', body),
    {
      // No retries: the start call returns placeholder ids at once (then we
      // poll), so a failure is fast. A retry would insert duplicate rows that
      // count against the visitor's daily limit.
      retry: 0,
    },
  );

  // Polls /api/public-completion-status for each pending output and patches
  // it in as soon as it's ready.
  const pollPendingCompletions = useCallback(
    async (items: CaptionItem[]) => {
      // ~13.5 min, longer than the server's maxDuration (300s).
      const MAX_ATTEMPTS = 270;
      const INTERVAL_MS = 3000;

      const patch = (id: number, change: Partial<CaptionItem>) =>
        setCaptions((current) =>
          current.map((caption) =>
            caption.id === id ? { ...caption, ...change } : caption,
          ),
        );

      try {
        await Promise.all(
          items.map(async (item) => {
            for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
              try {
                const res = await axiosInstance.post(
                  '/api/public-completion-status',
                  { uuid: item.uuid },
                  { skipErrorToast: true },
                );
                const { status, text } = res.data || {};
                if (status === 'done' && text) {
                  patch(item.id, { text, pending: false });
                  return;
                }
                if (status === 'failed' || status === 'content_policy') {
                  patch(item.id, { text: '', pending: false, error: true });
                  toast.error(
                    status === 'content_policy'
                      ? t(
                          'Your prompt or the generated output violates our content policy, so it could not be created.',
                        )
                      : t(
                          'One of your outputs failed to generate. Please try again.',
                        ),
                  );
                  return;
                }
              } catch {
                // transient poll error, keep trying
              }
              await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
            }
            patch(item.id, { pending: false, error: true });
            toast.error(
              t('This is taking longer than usual. Please try again later.'),
            );
          }),
        );
      } finally {
        setIsPolling(false);
      }
    },
    [t],
  );

  const hasUploads =
    Object.keys(uploadedFiles).length > 0 ||
    Object.values(uploadedMultiFiles).some(
      (files) => files && Object.keys(files).length > 0,
    );

  /**
   * Starts a generation. Resolves true once outputs were requested, so the
   * caller can bring them into view.
   */
  const generate = useCallback(async (): Promise<boolean> => {
    if (isFileLoading) {
      toast.error(t('Please wait until the file is uploaded'));
      return false;
    }

    if (!fields.get('keywords') && !fields.get('extra1') && !hasUploads) {
      const label = preset.fields?.find(
        (field) => field.name === 'keywords',
      )?.label;
      toast.error(
        `${t('Please provide the necessary information: ')}${
          label ? t(label) : ''
        }`,
      );
      return false;
    }

    if (reachedTotalLimit) {
      toast.error(
        t(
          'You have reached the maximum number of free generations across all tools. Please upgrade to continue.',
        ),
      );
      setShouldUpgrade(true);
      return false;
    }
    if (reachedTemplateLimit) {
      toast.error(
        t(
          preset.onlyForPaidPlans
            ? 'You have reached the maximum number of free generations. Please upgrade to generate more.'
            : 'You have reached the maximum number of free generations. Please create a free account to generate more.',
        ),
      );
      return false;
    }

    const body: Record<string, unknown> = {
      ...Object.fromEntries(fields),
      preset: slug,
      outputs: numberOfOutputs,
      language,
      files: Object.fromEntries(
        Object.entries(uploadedFiles).map(([name, file]) => [
          name,
          { name: file.name, path: file.path },
        ]),
      ),
      multiFiles: uploadedMultiFiles,
      // Placeholder rows + polling, so long generations don't die at the
      // ~120s Cloudflare edge timeout. The server falls back to a synchronous
      // answer when it can't create placeholders.
      async: 'true',
    };
    if (!body.keywords && hasUploads) body.keywords = 'See attached files';

    try {
      const response = await mutation.mutateAsync(body);
      const isPending = !!(
        response.data &&
        !Array.isArray(response.data) &&
        response.data.pending
      );
      const items: CaptionItem[] = isPending
        ? response.data.completions
        : response.data;

      const nextForTemplate = usedForTemplate + numberOfOutputs;
      const nextInTotal = usedInTotal + numberOfOutputs;
      setUsedForTemplate(nextForTemplate);
      setUsedInTotal(nextInTotal);
      writeStored(templateUsageKey, nextForTemplate);
      writeStored(totalUsageKey, nextInTotal);
      if (isPending) setIsPolling(true);

      setCaptions((current) => [
        ...items.map((item) => ({
          ...item,
          text: isPending ? '' : item.text,
          prompt: fields.get('keywords'),
          preset: slug,
          pending: isPending,
        })),
        ...current,
      ]);

      analytics.track('Public Output Generated', {
        preset: slug,
        outputs: numberOfOutputs,
        language,
        source: 'public',
      });

      if (isPending) void pollPendingCompletions(items);
      return true;
    } catch (error: any) {
      setIsPolling(false);
      const message = errorMessage(
        error?.response?.data?.error,
        error?.response?.status === 524
          ? 'This is taking longer than usual. Please try again in a couple of minutes.'
          : 'Something went wrong. Please try again later. If the error persists please contact us at support@easy-peasy.ai or via chat.',
      );
      // The axios interceptor may already have shown this error.
      if (!wasErrorToastShown(error)) toast.error(message);
      if (message.includes('Daily limit reached')) setShouldUpgrade(true);
      return false;
    }
  }, [
    analytics,
    fields,
    hasUploads,
    isFileLoading,
    language,
    mutation,
    numberOfOutputs,
    pollPendingCompletions,
    preset.fields,
    preset.onlyForPaidPlans,
    reachedTemplateLimit,
    reachedTotalLimit,
    slug,
    t,
    templateUsageKey,
    totalUsageKey,
    uploadedFiles,
    uploadedMultiFiles,
    usedForTemplate,
    usedInTotal,
  ]);

  const clearInputs = useCallback(() => {
    clearFields();
    Object.values(uploadedFiles).forEach((file) => {
      if (file.preview) URL.revokeObjectURL(file.preview);
    });
    setUploadedFiles({});
    setUploadedMultiFiles({});
  }, [clearFields, uploadedFiles]);

  return {
    fields,
    setField,
    clearInputs,
    language,
    setLanguage,
    numberOfOutputs,
    setNumberOfOutputs,
    maxOutputs,
    captions,
    isGenerating: mutation.isLoading || isPolling,
    generate,
    uploadedFiles,
    uploadedMultiFiles,
    uploadFile,
    removeFile,
    setMultiFiles,
    isFileLoading,
    runsLeft,
    reachedTemplateLimit,
    reachedTotalLimit,
    shouldUpgrade,
    setShouldUpgrade,
  };
}

export type PublicGeneration = ReturnType<typeof usePublicGeneration>;
